import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';

import RootView from '@/views/RootView';
import ProtectedRoute from '@/components/utility/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';

// auth route views (small, no lazy needed)
import AuthView from '@/views/auth';
import SignInView from '@/views/auth/SignIn';
import ForgotPasswordView from '@/views/auth/ForgotPassword';
import ResetPasswordEmail from '@/views/auth/ResetPasswordEmail';
import ResetPasswordView from '@/views/auth/ResetPassword';
import ResetPasswordSuccess from '@/views/auth/ResetPasswordSuccess';

import HomeView from '@/views/home';
import PermissionAwareNavigate from '@/components/utility/PermissionAwareNavigate';
import { Routes } from '@/constants/routes';
import { getPath } from '@/utils';
import GoogleOAuthComplete from '@/views/GoogleOAuthComplete';
import { HellSperre } from '@/theme/farbschema';

// --- Lazy-loaded views ---
const KundenUploadView = lazy(() => import('@/views/kundenUpload'));
const KundenTerminView = lazy(() => import('@/views/kundenTermin'));
const KundenCheckinView = lazy(() => import('@/views/kundenCheckin'));
const OeffentlicheBuchungView = lazy(() => import('@/views/oeffentlicheBuchung'));
const DashboardView = lazy(() => import('@/views/home/dashboard'));
const AdminView = lazy(() => import('src/views/home/admin'));
const MailTemplatesView = lazy(() => import('@/views/home/mailTemplates'));
const ApplicationTypesView = lazy(() => import('@/views/home/applicationTypes'));
const ApplicationProvidersView = lazy(() => import('@/views/home/applicationProviders'));
const ApplicationVpFieldsView = lazy(() => import('@/views/home/applicationVpFields'));
const ApplicationTemplatesView = lazy(() => import('@/views/home/applicationTemplates'));
const ApplicationAdminView = lazy(() => import('@/views/home/applicationAdmin'));
const UsersTeamsView = lazy(() => import('@/views/home/usersTeams'));
const TeamListView = lazy(() => import('@/views/home/teams'));
const TeamDetailView = lazy(() => import('@/views/home/teams/TeamDetailView'));
const PipelineView = lazy(() => import('@/views/home/pipeline'));
const MeetingListView = lazy(() => import('@/views/home/meetings'));
const NeuigkeitenView = lazy(() => import('@/views/home/neuigkeiten'));
const ProzesseView = lazy(() => import('@/views/home/prozesse'));
const MeetingDetailView = lazy(() => import('@/views/home/meetings/MeetingDetailView'));
const MeetingPoolListView = lazy(() => import('@/views/home/meetingPools'));
const MeetingPoolDetailView = lazy(() => import('@/views/home/meetingPools/MeetingPoolDetailView'));
const ApiKeyListView = lazy(() => import('@/views/home/apiKeys'));
const NotificationListView = lazy(() => import('@/views/home/notifications'));
const TodosView = lazy(() => import('@/views/home/todos'));
const CallcenterWizard = lazy(() => import('@/views/home/callcenter/CallcenterWizard'));
const DashboardTilesView = lazy(() => import('@/views/home/dashboard/DashboardTilesView'));
const ActivityLogView = lazy(() => import('@/views/home/activityLog'));

const ControllingView = lazy(() => import('@/views/home/controlling'));
const LernplattformView = lazy(() => import('@/views/home/lernplattform'));
const SprechstundenView = lazy(() => import('@/views/home/sprechstunden'));
const OrganigrammView = lazy(() => import('@/views/home/organigramm'));
const KiTrainerView = lazy(() => import('@/views/home/kiTrainer'));
const CustomersView = lazy(() => import('@/views/home/customer'));
const CustomerView = lazy(() => import('@/views/home/customer/CustomerView'));
const CustomerDetailsView = lazy(() => import('@/views/home/customer/CustomerDetailsView'));
const CustomerDetailView = lazy(() => import('@/views/home/customer/detail/CustomerDetailView'));
const CaseDetailView = lazy(() => import('@/views/home/cases/CaseDetailView'));
const CalendarView = lazy(() => import('@/views/home/calendar'));
const SegmentsListView = lazy(() => import('@/views/home/segments'));
const SegmentDetailView = lazy(() => import('@/views/home/segments/SegmentDetailPage'));
const SegmentFilterView = lazy(() => import('@/views/home/segments/SegmentFilterSeite'));

// Beratungsstrecke views
const LifePlanView = lazy(() => import('@/views/home/customer/plan'));
const SavingsPlanCalculator = lazy(() => import('@/views/home/customer/plan/SavingsPlanCalculator'));
const HousePayment = lazy(() => import('@/views/home/customer/plan/HousePayment'));
const LifeRoadPage = lazy(() => import('@/views/home/customer/plan/LifeRoadPage'));
const LifeRoadAnalyticsPage = lazy(() => import('@/views/home/customer/plan/LifeRoadAnalyticsPage'));
const EstgPage = lazy(() => import('@/views/home/customer/plan/EstgPage'));
const RetirementClientPage = lazy(() => import('@/views/home/customer/plan/RetirementClientPage'));
const DebtFreeCalculatorView = lazy(() => import('@/views/home/customer/plan/DebtFreeCalculator'));

const PannerView = lazy(() => import('@/views/home/customer/planner'));
const LifeInsuranceView = lazy(() => import('@/views/home/customer/planner/LifeInsurance'));
const BSCView = lazy(() => import('@/views/home/customer/planner/BSC'));
const KLVView = lazy(() => import('@/views/home/customer/planner/KLV'));
const SavingsIndividual = lazy(() => import('@/views/home/customer/planner/SavingsIndividual'));
const CalculatorView = lazy(() => import('@/views/home/customer/planner/Calculator'));
const ComparisonCalculatorView = lazy(() => import('@/views/home/customer/planner/ComparisonCalculator'));
const HealthInsuranceAnalysisView = lazy(() => import('@/views/home/customer/planner/HealthInsuranceAnalysis'));

const FinanceView = lazy(() => import('@/views/home/customer/finance'));
const FundingView = lazy(() => import('@/views/home/customer/finance/Funding'));
const FinanceInfoView = lazy(() => import('@/views/home/customer/finance/FinanceInfo'));

const HouseholdView = lazy(() => import('@/views/home/customer/household'));
const HouseholdCalculator = lazy(() => import('@/views/home/customer/household/HouseholdCalculator'));

const ConceptView = lazy(() => import('@/views/home/customer/concept'));
const ConceptTable = lazy(() => import('@/views/home/customer/concept/ConceptTable'));

const DocumentsView = lazy(() => import('@/views/home/customer/documents'));
const DocumentsTable = lazy(() => import('@/views/home/customer/documents/DocumentsTable'));

// --- Helpers ---

const LoadingFallback = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 200 }}>
    <CircularProgress sx={{ color: 'primary.main' }} size={32} />
  </Stack>
);

/** Wraps a lazy view with Suspense + route-level ErrorBoundary */
const Lazy = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary inline>
    <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
  </ErrorBoundary>
);

// --- Routes ---

const CustomerRoutes = [
  {
    index: true,
    element: <Navigate to={Routes.CustomerDetails} replace />,
  },
  {
    path: Routes.CustomerDetails,
    element: <Lazy><CustomerDetailsView /></Lazy>,
  },
  {
    path: Routes.LifePlan,
    element: <Lazy><LifePlanView /></Lazy>,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.LifeRoad} replace />,
      },
      {
        path: Routes.Diagram,
        element: <Lazy><SavingsPlanCalculator mode="pension" /></Lazy>,
      },
      {
        path: Routes.Children,
        element: <Lazy><SavingsPlanCalculator mode="children" /></Lazy>,
      },
      {
        path: Routes.HousePayment,
        element: <Lazy><HousePayment /></Lazy>,
      },
      {
        path: Routes.LifeRoad,
        element: <Lazy><LifeRoadPage /></Lazy>,
      },
      {
        path: Routes.LifeRoadAnalytics,
        element: <Lazy><LifeRoadAnalyticsPage /></Lazy>,
      },
      {
        path: Routes.Estg,
        element: <Lazy><EstgPage /></Lazy>,
      },
      {
        path: Routes.RetirementClient,
        element: <Lazy><RetirementClientPage /></Lazy>,
      },
      {
        path: Routes.DebtFreeCalculator,
        element: <Lazy><DebtFreeCalculatorView /></Lazy>,
      },
    ],
  },
  {
    path: Routes.Planner,
    element: <Lazy><PannerView /></Lazy>,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.LifeInsurance} replace />,
      },
      {
        path: Routes.LifeInsurance,
        element: <Lazy><LifeInsuranceView /></Lazy>,
      },
      {
        path: Routes.BSC,
        element: <Lazy><BSCView /></Lazy>,
      },
      {
        path: Routes.KLV,
        element: <Lazy><KLVView /></Lazy>,
      },
      {
        path: Routes.Savings,
        element: <Lazy><SavingsIndividual /></Lazy>,
      },
      {
        path: Routes.Calculator,
        element: <Lazy><CalculatorView /></Lazy>,
      },
      {
        path: Routes.HealthInsuranceAnalysis,
        element: <Lazy><HealthInsuranceAnalysisView /></Lazy>,
      },
      {
        path: Routes.ComparisonCalculator,
        element: <Lazy><ComparisonCalculatorView /></Lazy>,
      },
    ],
  },
  {
    path: Routes.Finance,
    element: <Lazy><FinanceView /></Lazy>,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.Funding} replace />,
      },
      {
        path: Routes.Funding,
        element: <Lazy><FundingView /></Lazy>,
      },
      {
        path: Routes.FinanceInfo,
        element: <Lazy><FinanceInfoView /></Lazy>,
      },
    ],
  },
  {
    path: Routes.Household,
    element: <Lazy><HouseholdView /></Lazy>,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.HouseholdCalculator} replace />,
      },
      {
        path: Routes.HouseholdCalculator,
        element: <Lazy><HouseholdCalculator /></Lazy>,
      },
    ],
  },
  {
    path: Routes.Concept,
    element: <Lazy><ConceptView /></Lazy>,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.ConceptInsurance} replace />,
      },
      {
        path: Routes.ConceptInsurance,
        element: <Lazy><ConceptTable /></Lazy>,
      },
    ],
  },
  {
    path: Routes.Documents,
    element: <Lazy><DocumentsView /></Lazy>,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.DocumentsTable} replace />,
      },
      {
        path: Routes.DocumentsTable,
        element: <Lazy><DocumentsTable /></Lazy>,
      },
    ],
  },
];

const router = createBrowserRouter([
  {
    path: Routes.Root,
    element: <RootView />,
    children: [
      {
        index: true,
        element: <Navigate replace to={Routes.Home} />,
      },
      {
        path: Routes.Home,
        element: (
          <ProtectedRoute>
            <HomeView />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <PermissionAwareNavigate
                to={[
                  [['callcenter'], Routes.Callcenter],
                  [['users', 'customers', 'teams'], Routes.Dashboard],
                  [['teams', 'contacts', 'meetings', 'pipeline', 'reports'], Routes.Controlling],
                  [['teams', 'contacts', 'meetings', 'meeting-pools'], Routes.Dashboard],
                  [['contacts', 'meetings', 'documents'], Routes.Dashboard],
                  [['customers', 'contacts'], Routes.Dashboard],
                ]}
                replace
              />
            ),
          },
          {
            path: Routes.Dashboard,
            element: <Lazy><DashboardTilesView /></Lazy>,
          },
          {
            path: Routes.Cockpit,
            element: <Lazy><DashboardView /></Lazy>,
          },
          {
            path: Routes.Controlling,
            element: <Lazy><ControllingView /></Lazy>,
          },
          {
            path: Routes.Lernplattform,
            element: <Lazy><LernplattformView /></Lazy>,
          },
          {
            // Sprechstunden-Matrix als eigene Seite (Entscheid Alex 05.08.2026).
            // Erreichbar ueber die Navigations-Kachel auf der Kachelseite.
            // Kein Rollen-Gate an der Route: Lesen darf jede angemeldete Rolle,
            // die Schreibrechte entscheidet die ConsultationSlotPolicy.
            path: Routes.Sprechstunden,
            element: <Lazy><SprechstundenView /></Lazy>,
          },
          {
            // Ansprechpartnerliste als Ziel der Dashboard-Kachel (Decision
            // #237). Kein Rollen-Gate an der Route: lesen darf jede
            // angemeldete Rolle ausser `telefonie` — die sperrt der Server.
            path: Routes.Organigramm,
            element: <Lazy><OrganigrammView /></Lazy>,
          },
          {
            path: Routes.KiTrainer,
            element: <Lazy><KiTrainerView /></Lazy>,
          },
          {
            path: Routes.Admin,
            element: <Navigate to={`/${Routes.Home}/${Routes.UsersTeams}?tab=users`} replace />,
          },
          {
            path: Routes.UsersTeams,
            element: <Lazy><UsersTeamsView /></Lazy>,
          },
          {
            path: Routes.MailTemplates,
            element: <Lazy><MailTemplatesView /></Lazy>,
          },
          {
            path: Routes.ApplicationTypes,
            element: <Lazy><ApplicationTypesView /></Lazy>,
          },
          {
            path: Routes.ApplicationProviders,
            element: <Lazy><ApplicationProvidersView /></Lazy>,
          },
          {
            path: Routes.ApplicationVpFields,
            element: <Lazy><ApplicationVpFieldsView /></Lazy>,
          },
          {
            path: Routes.ApplicationTemplates,
            element: <Lazy><ApplicationTemplatesView /></Lazy>,
          },
          {
            path: Routes.ApplicationAdmin,
            element: <Lazy><ApplicationAdminView /></Lazy>,
          },
          {
            path: Routes.ActivityLog,
            element: <Lazy><ActivityLogView /></Lazy>,
          },

          {
            path: Routes.Teams,
            element: <Lazy><TeamListView /></Lazy>,
          },
          {
            path: 'teams/:id',
            element: <Lazy><TeamDetailView /></Lazy>,
          },
          {
            path: Routes.Deals,
            element: <Lazy><PipelineView /></Lazy>,
          },
          {
            path: Routes.Meetings,
            element: <Lazy><MeetingListView /></Lazy>,
          },
          {
            path: Routes.Neuigkeiten,
            element: <Lazy><NeuigkeitenView /></Lazy>,
          },
          // Prozessdoku (Read-only). Die Schranke sitzt im Backend
          // (role:sa); der Menuepunkt steht nur in der SA-Navigation.
          {
            path: Routes.Prozesse,
            element: <Lazy><ProzesseView /></Lazy>,
          },
          {
            path: 'meetings/:id',
            element: <Lazy><MeetingDetailView /></Lazy>,
          },
          {
            path: Routes.MeetingPools,
            element: <Lazy><MeetingPoolListView /></Lazy>,
          },
          {
            path: 'meeting-pools/:id',
            element: <Lazy><MeetingPoolDetailView /></Lazy>,
          },
          {
            path: Routes.ApiKeys,
            element: <Lazy><ApiKeyListView /></Lazy>,
          },
          {
            path: Routes.Customers,
            element: <Lazy><CustomersView /></Lazy>,
          },
          {
            path: Routes.Notifications,
            element: <Lazy><NotificationListView /></Lazy>,
          },
          {
            path: Routes.Todos,
            element: <Lazy><TodosView /></Lazy>,
          },
          {
            path: Routes.Callcenter,
            element: <Lazy><CallcenterWizard /></Lazy>,
          },
          {
            path: Routes.CustomerDetail,
            element: <Lazy><CustomerDetailView /></Lazy>,
          },
          {
            path: Routes.Calendar,
            element: <Lazy><CalendarView /></Lazy>,
          },
          {
            path: Routes.Segments,
            element: <Lazy><SegmentsListView /></Lazy>,
          },
          {
            path: 'segments/neu',
            element: <Lazy><SegmentFilterView /></Lazy>,
          },
          {
            path: 'segments/:id/filter',
            element: <Lazy><SegmentFilterView /></Lazy>,
          },
          {
            path: 'segments/:id',
            element: <Lazy><SegmentDetailView /></Lazy>,
          },
          {
            path: Routes.CaseDetail,
            element: <Lazy><CaseDetailView /></Lazy>,
          },
          {
            path: '*',
            element: <Navigate to={`/${Routes.Home}`} replace />,
          },
        ],
      },
      {
        path: getPath(Routes.Home, Routes.Customers, Routes.Customer),
        element: (
          <ProtectedRoute>
            <HellSperre><Lazy><CustomerView /></Lazy></HellSperre>
          </ProtectedRoute>
        ),
        children: CustomerRoutes,
      },
      {
        path: Routes.Auth,
        element: (
          <ProtectedRoute authFlow>
            <HellSperre><AuthView /></HellSperre>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate replace to={Routes.SignIn} />,
          },
          {
            path: Routes.SignIn,
            element: <SignInView />,
          },
          {
            path: Routes.ForgotPassword,
            element: <ForgotPasswordView />,
          },
          {
            path: `${Routes.PasswordReset}/:token`,
            element: <ResetPasswordView />,
          },
        ],
      },
      {
        path: getPath(Routes.Auth, Routes.ResetPasswordEmail),
        element: <HellSperre><ResetPasswordEmail /></HellSperre>,
      },
      {
        path: getPath(Routes.Auth, Routes.ResetPasswordSuccess),
        element: <HellSperre><ResetPasswordSuccess /></HellSperre>,
      },
      {
        path: Routes.GoogleOAuthComplete,
        element: <HellSperre><GoogleOAuthComplete /></HellSperre>,
      },
    ],
  },
  // Oeffentliche Kunden-Upload-Seite (Decision #126): bewusst als EIGENES
  // Top-Level-Objekt neben Root — so greift weder RootView/ScreenWrapper noch
  // ProtectedRoute. Login-los, das globale Theme (index.tsx) ist trotzdem aktiv.
  {
    path: `${Routes.Unterlagen}/:meetingId`,
    element: (
      <HellSperre>
        <Suspense
          fallback={
            <Stack sx={{ height: '100vh' }} alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }
        >
          <KundenUploadView />
        </Suspense>
      </HellSperre>
    ),
  },
  // Oeffentliche Kunden-Self-Service-Seiten (Decision #65). Gleiches Muster wie
  // oben: eigene Top-Level-Objekte neben Root, damit weder RootView/
  // ScreenWrapper noch ProtectedRoute greifen. Zwei Pfade statt eines mit
  // Parameter, weil jeder seine EIGENE Link-Signatur traegt.
  {
    path: `${Routes.Termin}/:meetingId/absagen`,
    element: (
      <HellSperre>
        <Suspense
          fallback={
            <Stack sx={{ height: '100vh' }} alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }
        >
          <KundenTerminView aktion="absagen" />
        </Suspense>
      </HellSperre>
    ),
  },
  {
    path: `${Routes.Termin}/:meetingId/umbuchen`,
    element: (
      <HellSperre>
        <Suspense
          fallback={
            <Stack sx={{ height: '100vh' }} alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }
        >
          <KundenTerminView aktion="umbuchen" />
        </Suspense>
      </HellSperre>
    ),
  },
  // Check-in-Durchleitung (Decision #217): signierter Link aus der Termin-Mail,
  // leitet im Terminfenster in den gebuchten Zoom-Raum weiter. Top-Level wie
  // die Self-Service-Seiten darueber — kein Login, keine Shell.
  {
    path: `${Routes.Termin}/:meetingId/checkin`,
    element: (
      <HellSperre>
        <Suspense
          fallback={
            <Stack sx={{ height: '100vh' }} alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }
        >
          <KundenCheckinView />
        </Suspense>
      </HellSperre>
    ),
  },
  // Oeffentliche, login-freie Buchungsseite (Stufe 1, Entscheide Alex E1-E6 vom
  // 04.08.2026). Gleiches Muster wie die beiden Seiten darueber: eigenes
  // Top-Level-Objekt neben Root, damit weder RootView/ScreenWrapper noch
  // ProtectedRoute greifen. `?source=` liest die Seite selbst aus der Query.
  {
    path: `${Routes.Buchen}/:slug`,
    element: (
      <HellSperre>
        <Suspense
          fallback={
            <Stack sx={{ height: '100vh' }} alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }
        >
          <OeffentlicheBuchungView />
        </Suspense>
      </HellSperre>
    ),
  },
]);

export default router;
